import { Button } from '@components/ui/button';
import * as linkify from 'linkifyjs';

import { useMemo, useState } from 'react';

import Surface from '../surface';

export type LinkEditorPanelProps = {
	initialUrl?: string;
	initialOpenInNewTab?: boolean;
	onSetLink: (url: string, openInNewTab?: boolean) => void;
	allowEmpty?: boolean;
	onClose?: () => void;
};

export const useLinkEditorState = ({
	initialUrl,
	initialOpenInNewTab,
	onSetLink,
	allowEmpty = false,
	onClose,
}: LinkEditorPanelProps) => {
	const [url, setUrl] = useState(initialUrl || '');
	const [openInNewTab, setOpenInNewTab] = useState(initialOpenInNewTab || false);

	const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const updatedUrl = event.target.value;
		setUrl(updatedUrl);
	};

	const isValidUrl = useMemo(() => linkify.test(url), [url]);
	const isUrlChanged = useMemo(() => url !== initialUrl, [url, initialUrl]);
	const shouldShowApply = useMemo(() => isValidUrl && isUrlChanged, [isValidUrl, isUrlChanged]);
	const shouldShowDelete = useMemo(() => !!initialUrl, [initialUrl]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (isValidUrl) {
			onSetLink(url, openInNewTab);
			onClose?.();
		}
	};

	const handleDelete = () => {
		if (allowEmpty) {
			setUrl('');
			onSetLink('', openInNewTab);
			onClose?.();
		}
	};

	return {
		url,
		setUrl,
		openInNewTab,
		setOpenInNewTab,
		onChange,
		handleSubmit,
		handleDelete,
		isValidUrl,
		shouldShowApply,
		shouldShowDelete,
	};
};

export const LinkEditorPanel = ({
	onSetLink,
	initialOpenInNewTab,
	initialUrl,
	allowEmpty = false,
	onClose,
}: LinkEditorPanelProps) => {
	const state = useLinkEditorState({
		onSetLink,
		initialOpenInNewTab,
		initialUrl,
		allowEmpty,
		onClose,
	});

	return (
		<Surface className="p-2">
			<form
				onSubmit={state.handleSubmit}
				className="flex w-96 items-center gap-2"
			>
				<div className="flex flex-1 cursor-text items-center gap-2 rounded-md bg-accent">
					<div className="flex flex-1 items-center gap-2 p-2">
						<span className="text-secondary">
							<svg
								width="16px"
								height="16px"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M12.7076 18.3639L11.2933 19.7781C9.34072 21.7308 6.1749 21.7308 4.22228 19.7781C2.26966 17.8255 2.26966 14.6597 4.22228 12.7071L5.63649 11.2929M18.3644 12.7071L19.7786 11.2929C21.7312 9.34024 21.7312 6.17441 19.7786 4.22179C17.826 2.26917 14.6602 2.26917 12.7076 4.22179L11.2933 5.636M8.50045 15.4999L15.5005 8.49994"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</span>
						<input
							type="text"
							className="w-full flex-1 bg-transparent text-sm text-black outline-none dark:text-white"
							placeholder="Enter link"
							value={state.url}
							onChange={state.onChange}
						/>
					</div>
					{state.shouldShowApply && (
						<Button
							className="h-9 text-primary-blue hover:text-primary-blue-hover hover:no-underline"
							variant="link"
							type="submit"
						>
							Apply
						</Button>
					)}
				</div>
				{state.shouldShowDelete && (
					<Button
						size="sm"
						type="button"
						variant="icon"
						className="px-1"
						onClick={state.handleDelete}
					>
						<svg
							width="16px"
							height="16px"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M16 6V5.2C16 4.0799 16 3.51984 15.782 3.09202C15.5903 2.71569 15.2843 2.40973 14.908 2.21799C14.4802 2 13.9201 2 12.8 2H11.2C10.0799 2 9.51984 2 9.09202 2.21799C8.71569 2.40973 8.40973 2.71569 8.21799 3.09202C8 3.51984 8 4.0799 8 5.2V6M3 6H21M19 6V17.2C19 18.8802 19 19.7202 18.673 20.362C18.3854 20.9265 17.9265 21.3854 17.362 21.673C16.7202 22 15.8802 22 14.2 22H9.8C8.11984 22 7.27976 22 6.63803 21.673C6.07354 21.3854 5.6146 20.9265 5.32698 20.362C5 19.7202 5 18.8802 5 17.2V6"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</Button>
				)}
			</form>
		</Surface>
	);
};
